import { Injectable } from '@angular/core';
import { TownsHttpService } from './towns.http.service';
import { objectToCamel, objectToSnake } from 'ts-case-convert';
import { lastValueFrom } from 'rxjs';
import { IBase, IFindResult } from 'src/app/models';

@Injectable({
  providedIn: 'root',
})
export class TownsService {
  constructor(private townsHttpService: TownsHttpService) {}

  async getTown(townId: number) {
    const source$ = this.townsHttpService.getTown(townId);
    const response = await lastValueFrom(source$);
    const town = objectToCamel(response as object);
    return town;
  }

  async createTown(body: object) {
    const source$ = this.townsHttpService.createTown(objectToSnake(body));
    const response = await lastValueFrom(source$);
    return objectToCamel(response as object);
  }

  async updateTownConfig(townId: number, body: object) {
    const source$ = this.townsHttpService.updateTownConfig(townId, objectToSnake(body));
    const response = await lastValueFrom(source$);
    return objectToCamel(response as object);
  }

  async deleteTown(townId: number) {
    const source$ = this.townsHttpService.deleteTown(townId);
    await lastValueFrom(source$);
  }

  async getTowns(page?: number, size?: number) {
    const source$ = this.townsHttpService.getTowns(page, size);
    const response = await lastValueFrom(source$);
    const towns = objectToCamel(response as object);
    const townsArray = this.getData(towns);
    return townsArray;
  }

  async getTownStops(townId: number, clientId: number, routeId?: number): Promise<any[]> {
    const source$ = this.townsHttpService.getTownStops(townId, clientId, routeId);
    const response: any = await lastValueFrom(source$);
    return response ?? [];
  }

  async filterTowns(page?: number, size?: number, searchValue?: string) {
    const source$ = this.townsHttpService.filterTowns(page, size, searchValue);
    const response = await lastValueFrom(source$);
    const towns = objectToCamel(response as object);
    const townsArray = this.getData(towns);
    return townsArray;
  }

  // The town services payload is heavy and several map redraws ask for the same town at once
  private servicesRequestsInFlight = new Map<number, Promise<any>>();

  async getServices(townId: number) {
    const inFlight = this.servicesRequestsInFlight.get(townId);
    if (inFlight) return inFlight;
    const request = (async () => {
      const source$ = this.townsHttpService.getServices(townId);
      const response = await lastValueFrom(source$);
      const serviceInfos = objectToCamel(response as object);
      return this.getData(serviceInfos);
    })().finally(() => this.servicesRequestsInFlight.delete(townId));
    this.servicesRequestsInFlight.set(townId, request);
    return request;
  }

  async getServicesList(townId: number, page?: number, size?: number) {
    const source$ = this.townsHttpService.getServicesList(townId, page, size);
    const response = await lastValueFrom(source$);
    const serviceInfos = objectToCamel(response as object);
    const serviceInfoArray = this.getData(serviceInfos);
    return serviceInfoArray;
  }

  async getStopsFrom(townId: number, stopId?: number, dateTime?: Date, mergedStops?: boolean, routeId?: number, lat?: number, lng?: number, applyRestrictions?: boolean) {
    const source$ = this.townsHttpService.getStopsFrom(townId, stopId, dateTime, mergedStops, routeId, lat, lng, applyRestrictions);
    const response = await lastValueFrom(source$);
    const stops = objectToCamel(response as object[]);
    return stops;
  }

  async getAvailabilities(id: number, userId: number, inStopId: number, outStopId: number, seats: number, prmSeats: number, dateTimes: any, isArrivalTime: boolean = false, reservationId?: number, isSingleReservation?: boolean) {
    const source$ = this.townsHttpService.getAvailabilities(id, userId, inStopId, outStopId, seats, prmSeats, dateTimes, isArrivalTime, reservationId, isSingleReservation);
    const response = await lastValueFrom(source$);
    const availabilities = objectToCamel(response as IFindResult);
    return availabilities;
  }

  async getAvailabilitiesPositions(id: number, userId: number, inStopId: number | undefined, inLatitude: number, inLongitude: number, inName: string, outStopId: number | undefined, outLatitude: number, outLongitude: number, outName: string, seats: number, prmSeats: number, dateTimes: any, isArrivalTime: boolean = false, reservationId?: number, isSingleReservation?: boolean) {
    const source$ = this.townsHttpService.getAvailabilitiesByPositions(id, userId, inStopId, inLatitude, inLongitude, inName, outStopId, outLatitude, outLongitude, outName, seats, prmSeats, dateTimes, isArrivalTime, reservationId);
    const response = await lastValueFrom(source$);
    const availabilities = objectToCamel(response as IFindResult);
    return availabilities;
  }

  async getRoutesWithStop(townId: number, stopId: number, excludeRouteId?: number): Promise<IBase[]> {
    const source$ = this.townsHttpService.getRoutesWithStop(townId, stopId, excludeRouteId);
    const response = await lastValueFrom(source$);
    return objectToCamel((response ?? []) as object[]) as IBase[];
  }

  async getLatestFeatureId(townId: number) {
    const source$ = this.townsHttpService.getLatestFeatureId(townId);
    const response = await lastValueFrom(source$) as number;
    return response;
  }

  getData(response: any) {
    return response.data;
  }
}
